import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { StaticryptPassphraseDialogService } from '../../services/staticrypt-passphrase-dialog.service';
import {
  PASSPHRASE_MAX,
  PASSPHRASE_MIN,
  validatePassphrasePair,
} from '../../utils/staticrypt-crypto';

/** PAT-284 — Ionic-styled passphrase + confirm dialog for StatiCrypt export / PAT-305 send. */
@Component({
  selector: 'app-staticrypt-passphrase-dialog',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './staticrypt-passphrase-dialog.component.html',
  styleUrl: './staticrypt-passphrase-dialog.component.scss',
})
export class StaticryptPassphraseDialogComponent {
  readonly dialog = inject(StaticryptPassphraseDialogService);

  passphrase = signal('');
  confirm = signal('');
  error = signal('');

  readonly minLen = PASSPHRASE_MIN;
  readonly maxLen = PASSPHRASE_MAX;

  readonly titleText = computed(() =>
    this.dialog.purpose() === 'send' ? 'Password-protect & send' : 'Password-protect download',
  );

  readonly messageText = computed(() =>
    this.dialog.purpose() === 'send'
      ? `Choose a passphrase (${this.minLen}–${this.maxLen} characters). Recipients need it in Pathways-Intelligence and to open the protected artifact. This is not stored in chat.`
      : `Choose a passphrase (${this.minLen}–${this.maxLen} characters). Recipients need it to open the file. This is not stored in chat.`,
  );

  readonly confirmLabel = computed(() =>
    this.dialog.purpose() === 'send' ? 'Send protected' : 'Download protected HTML',
  );

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (!this.dialog.active()) return;
    this.onCancel();
  }

  onBackdropClick(): void {
    this.onCancel();
  }

  onCancel(): void {
    this.resetFields();
    this.dialog.resolve(null);
  }

  onConfirm(): void {
    const check = validatePassphrasePair(this.passphrase(), this.confirm());
    if (!check.ok) {
      this.error.set(check.message);
      return;
    }
    const value = this.passphrase();
    this.resetFields();
    this.dialog.resolve(value);
  }

  private resetFields(): void {
    this.passphrase.set('');
    this.confirm.set('');
    this.error.set('');
  }
}
